import React, { useState } from 'react';
import { signInWithGoogle } from '../../services/auth';

interface GoogleSignInButtonProps {
    // Surface a failure in the parent form's existing error area.
    onError: (message: string) => void;
    // Disabled while the parent form has its own submit in flight.
    disabled?: boolean;
    // "Log in with Google" on login, "Sign up with Google" on register.
    label?: string;
}

// Google's official multi-colour "G" mark, 17px.
function GoogleLogo() {
    return (
        <svg width="17" height="17" viewBox="0 0 18 18" aria-hidden="true" className="flex-none">
            <path fill="#4285F4" d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9086c1.7018-1.5668 2.6837-3.874 2.6837-6.615z" />
            <path fill="#34A853" d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9086-2.2581c-.8059.54-1.8368.859-3.0478.859-2.344 0-4.3282-1.5831-5.036-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z" />
            <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2822-1.71V4.9582H.9573C.3477 6.1732 0 7.5477 0 9s.3477 2.8268.9573 4.0418L3.964 10.71z" />
            <path fill="#EA4335" d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4636.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.964 7.29C4.6718 5.1627 6.656 3.5795 9 3.5795z" />
        </svg>
    );
}

// The grey secondary button under the email form — icon + label centred on an
// rgb(239,239,239) fill, 10px radius, full width.
function GoogleSignInButton({ onError, disabled, label = 'Log in with Google' }: GoogleSignInButtonProps) {
    const [isLoading, setIsLoading] = useState(false);

    const handleClick = async () => {
        setIsLoading(true);
        onError('');

        try {
            await signInWithGoogle();
            // Success is handled by the auth state listener.
        } catch (err: any) {
            // The user dismissing the popup isn't a real error — stay quiet.
            const code = err?.code;
            if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
                onError(err?.message || 'Failed to sign in with Google');
            }
            setIsLoading(false);
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={disabled || isLoading}
            className="flex w-full items-center justify-center gap-2.5 rounded-btn bg-surface-muted px-4 py-[13px] text-ui font-semibold text-ink-secondary transition hover:bg-surface-muted-hover disabled:opacity-50"
        >
            <GoogleLogo />
            {isLoading ? 'Signing in…' : label}
        </button>
    );
}

export default GoogleSignInButton;
